import { FC, useEffect, useState } from 'react';

import { Button } from '@/components/ui';
import { BUTTON_SIZES } from '@/components/ui/Buttons/utils';
import interpolate from '@/utils/translations/interpolate';
import { TypeSubFlowV2Translations } from '@/utils/translations/TypeSubFlowV2Translations';
import { getBundleDiscountValue, getChargeMeta } from '../utils';
import { Upsell } from '../Upsell';
import { getStep } from '@/utils/subflowAnalyticsHelpers';
import { SUBFLOW_PATHS } from '@/core/constants';
import { addLineBreaksAroundParentheses, formatMoney } from '@/utils/helpers';
import { SubFlowV3ContextData } from '../../context/SubFlowV3Context';
import { usePathname } from '@/navigation';
import useLocale from '@/hooks/useLocale';
import { _getBaseZendeskLink } from '@/utils/zendesk';
import useFeature from '@/hooks/useFeature';
import { useFlags } from 'launchdarkly-react-client-sdk';
import classNames from 'classnames';

export interface SubmitFooterProps {
  data: SubFlowV3ContextData;
  customBtnId?: string;
  isLoading?: boolean;
  onSubmit: () => void;
  reviewOrder?: boolean;
  showLegal?: boolean;
  showPaymentMsg?: boolean;
  showShipping?: boolean;
  showTotal?: boolean;
  showUpsell?: boolean;
  hasUpcomingProducts?: boolean;
  t: TypeSubFlowV2Translations;
  showShippingCopy?: boolean;
  classes?: {
    container?: string;
    legal?: string;
    ctaWrapper?: string;
  };
  isSimplifiedSubflow?: boolean;
}

const SubmitFooter: FC<SubmitFooterProps> = ({
  data,
  customBtnId,
  isLoading,
  onSubmit,
  reviewOrder,
  showLegal,
  showPaymentMsg,
  showShipping,
  showTotal,
  showUpsell,
  hasUpcomingProducts = true,
  t,
  showShippingCopy,
  classes,
  isSimplifiedSubflow,
}) => {
  const isBfcmEnabled = useFeature('money_bfcm-mode-2025');
  const path = usePathname() || '';
  const locale = useLocale();
  const { charges, total, subtotal } = getChargeMeta(data, t as any);
  const { stepNum } = getStep(path) || {};
  const [ctaClass, setctaClass] = useState('');

  const { 'sub-squad-enable-tariff': subSquadEnableTariff } = useFlags();

  // Calculate bundle discount based on selected option for BFCM 2025
  const bundleDiscountValue = getBundleDiscountValue(
    data.paymentOption || null,
    data.bundleDiscountPreview?.bundle_discount_preview.bundles,
    data.subscriptionAddOns.length > 0,
  );
  const bundleDiscountAmount = total * (bundleDiscountValue / 100);
  const subtotalWithBundleDiscount = total - bundleDiscountAmount;

  const subtotalAmount = isBfcmEnabled ? subtotalWithBundleDiscount : subtotal;
  const hideDecimals = isBfcmEnabled ? false : true;
  const showDecimalsToFloat = isBfcmEnabled ? true : false;

  let buttonText = t.childInfoButton;

  if (reviewOrder) {
    buttonText = t.reviewOrderButton;
  }

  if (showPaymentMsg) {
    buttonText = t.completeOrderButton;
  }

  const referralLineItem = data.referralCode && (
    <div className="mb-4 flex justify-between text-base lg:text-lg">
      <div>{t.referralCredit}</div>
      <div>{t.calculatedCheckout}</div>
    </div>
  );

  const bundleDiscountItem = isBfcmEnabled && (
    <div className="mb-4 flex justify-between text-base lg:text-lg font-bold text-red-v2">
      <div>{t.bfcm2024OrderReviewFooter}</div>
      <div>
        -
        {formatMoney(
          bundleDiscountAmount / 100,
          locale,
          hideDecimals,
          showDecimalsToFloat,
        )}
      </div>
    </div>
  );

  useEffect(() => {
    const ctaClass = path.includes(SUBFLOW_PATHS.ORDER_REVIEW)
      ? 'subflow-continue-to-checkout'
      : `next-to-step-${(stepNum as number) + 1}`;

    setctaClass(ctaClass);
  }, [stepNum, path]);

  return (
    <div
      className={`flex flex-col justify-center ${hasUpcomingProducts ? 'border-t border-gray-400 lg:!mt-0' : ''} pt-1 lg:mt-10 lg:flex-row lg:gap-x-32 lg:pt-8 ${classes?.container || ''}`}
    >
      {!isSimplifiedSubflow && showLegal && (
        <div
          className={`order-2 w-full flex-none text-sm md:text-base lg:order-1 lg:w-[60%] lg:pr-20 flex-grow-0 flex-shrink flex-auto ${classes?.legal || ''}`}
          dangerouslySetInnerHTML={{
            __html: interpolate(t.childInfoHelp, {
              contactUsLink: `<a class="text-indigo" href=${locale === 'en-US' ? 'https://lovevery.com/pages/disability' : _getBaseZendeskLink(locale)}>${t.contactLabel}</a>`,
            }),
          }}
        />
      )}
      {showUpsell && (
        <div className="w-full flex-none text-sm md:text-base lg:order-1 lg:w-[60%]">
          <Upsell t={t} />
        </div>
      )}
      <div
        className={`order-1 my-2 w-full min-w-[270px] md:px-6 lg:order-2 lg:my-0 lg:mb-10 lg:max-w-[400px] lg:flex-auto lg:px-0 lg:ml-auto ${classes?.ctaWrapper || ''}`}
      >
        {showTotal && data.paymentOption && (
          <div className="mb-4 w-full md:mb-6 md:border-b md:border-gray-400">
            {charges &&
              charges.length > 0 &&
              charges.map((charge) => {
                const chargeName = addLineBreaksAroundParentheses(charge.name);
                const chargeHasSavings = 'savings' in charge;
                const price =
                  isBfcmEnabled && chargeHasSavings
                    ? charge.price + charge.savings
                    : charge.price;
                return (
                  <div
                    className="mb-4 flex justify-between text-base gap-x-12 lg:text-lg"
                    key={charge.name}
                  >
                    <div className="whitespace-pre-line md:whitespace-normal">
                      {chargeName}
                    </div>
                    <div>
                      {formatMoney(
                        price / 100,
                        locale,
                        hideDecimals,
                        showDecimalsToFloat,
                      )}
                    </div>
                  </div>
                );
              })}
            {referralLineItem}
            {bundleDiscountItem}
            <div className="flex justify-between border-t border-gray-400 py-6 text-lg font-bold lg:text-2xl">
              <div>{t.subtotal}</div>
              <div>{formatMoney(subtotalAmount / 100, locale, true)}</div>
            </div>
          </div>
        )}
        {showShipping && data.paymentOption && (
          <div
            className={classNames('border-gray-400 ', {
              'border-b mb-6': subSquadEnableTariff,
            })}
          >
            {bundleDiscountItem}
            {subSquadEnableTariff ? (
              <div className="px-0 pb-5 pt-2">
                <span className="text-base">
                  {t.orderReviewCalculationCaption}
                </span>
              </div>
            ) : (
              <>
                <div className="mb-4 flex justify-between text-base lg:text-lg">
                  <div>{t.shipping}</div>
                  <div>{t.freeShipping}</div>
                </div>
                <div className="mb-4 flex justify-between text-base lg:text-lg">
                  <div>{t.taxes}</div>
                  <div className="pl-6 text-right">{t.calculatedCheckout}</div>
                </div>
              </>
            )}
            {referralLineItem}
            <div className="flex justify-between border-t border-gray-400 py-6 text-lg font-bold lg:text-2xl">
              <div>{t.subtotal}</div>
              <div>
                {formatMoney(
                  subtotalAmount / 100,
                  locale,
                  hideDecimals,
                  showDecimalsToFloat,
                )}
              </div>
            </div>
          </div>
        )}
        <Button
          classes={`btn-responsive h-14 ${ctaClass}`}
          id={`${customBtnId}-submit-footer`}
          ctaText={buttonText}
          disabled={isLoading}
          fullWidth
          includeSpinner={isLoading}
          onClick={onSubmit}
          size={BUTTON_SIZES.lg}
        />
        {isBfcmEnabled && (
          <div className="mt-4 text-base lg:text-lg text-center text-red-v2">
            <p>{t.bfcm2024OrderReviewFooterSavings}</p>
          </div>
        )}
        {showShippingCopy && (
          <div className="mt-4 text-sm">
            <div className="mt-4">{t.freeShippingAbout}</div>
          </div>
        )}
        {isSimplifiedSubflow && (
          <div className="mt-6 text-center">
            <p>{t.startingPlanExplanation}</p>
            <p className="mt-8">{t.startingPaymentExplanation}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitFooter;
